import React from 'react'
import styles from './Filmstrip.scss'
import cx from 'classnames'
import * as urlHelpers from 'lib/urlHelpers'
import _ from 'lodash'
import preloadImg from 'lib/preloadImg'
import {CircleSpinner} from 'common/spinners'
import shallowCompare from 'react-addons-shallow-compare'
import {clamp} from 'lib/utils'

export const Filmstrip = ({photos, activePhoto, onPhotoClick, mouseMode}) => (
  <div className={styles.Filmstrip}>
    {photos.map((p, index) => (
      <Thumbnail photo={p} key={p.id}
        index={index}
        onClick={onPhotoClick}
        active={index === activePhoto}
        mouseMode={mouseMode} />
    ))}
  </div>
)

// lazy-loading thumbnail, with offscreen buffer
class Thumbnail extends React.Component {
  constructor (props) {
    super(props)
    this.state = {loaded: false}
    this.throttledCheck = _.throttle(this.checkIfLoad, 100)
  }

  componentDidMount () {
    if (this.props.active) {
      this.checkIfScroll()
    }
    // do this async to not block the rest of the render cycle
    setTimeout(this.checkIfLoad, 0)
    this._parentNode.addEventListener('scroll', this.throttledCheck)
    window.addEventListener('resize', this.throttledCheck)
  }

  shouldComponentUpdate (nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState)
  }

  componentDidUpdate (prevProps) {
    if (this.props.active && prevProps.active !== this.props.active) {
      if (this.props.mouseMode) {
        this.checkIfScroll()
      } else {
        this.scrollCenter()
      }
    }
    setTimeout(this.checkIfLoad(), 0)
  }

  componentWillUnmount () {
    this._parentNode.removeEventListener('scroll', this.throttledCheck)
    window.removeEventListener('resize', this.throttledCheck)
    this._parentNode = null
  }

  checkIfLoad = () => {
    if (this.state.loaded) { return }
    if (!this._placeholder) { return }
    const THRESHOLD = 1000
    // get how far outside the viewport our placeholder is
    const rect = this._placeholder.getBoundingClientRect()
    const inZone = (x, y) => {
      return (
        x > -THRESHOLD &&
        x < window.innerWidth + THRESHOLD &&
        y > -THRESHOLD &&
        y < window.innerHeight + THRESHOLD
      )
    }
    // if it's in the window + padding, load the thing
    if (inZone(rect.left, rect.top) || inZone(rect.right, rect.bottom)) {
      this.loadImg()
    }
  }

  checkIfScroll = () => {
    // if the active thumbnail is offscreen, scroll it into view
    const rect = this._placeholder.getBoundingClientRect()
    if (rect.right < 0 || rect.left > window.innerWidth) {
      this.scrollCenter()
    }
  }

  scrollCenter = () => {
    if (this._parentNode.scrollWidth <= this._parentNode.clientWidth) { return } // no scrolling needed if no overflow
    const scroll = (this._placeholder.parentNode.offsetLeft) - (this._parentNode.offsetWidth - this._placeholder.clientWidth) / 2
    this._parentNode.scrollLeft = clamp(0, this._parentNode.scrollWidth - this._placeholder.clientWidth, scroll)
  }

  loadImg = () => {
    preloadImg(this.url()).then(() => {
      this.setState({loaded: true})
    })
  }

  setRef = (placeholder) => {
    this._placeholder = placeholder
    if (placeholder) {
      this._parentNode = placeholder.parentNode.parentNode
    }
  }

  url = () => {
    return urlHelpers.photoCenti(this.props.photo.id)
  }

  onClick = () => {
    const {index, onClick} = this.props
    onClick(index)
  }

  render () {
    const {loaded} = this.state
    const {active} = this.props
    if (loaded) {
      return <div className={cx(styles.thumbnail, active && styles.active)}>
        <img ref={this.setRef} src={this.url()} onClick={this.onClick} />
      </div>
    }

    return (
      <div className={cx(styles.thumbnail, active && styles.active, styles.thumbPlaceholder)}>
        <img ref={this.setRef}
          src='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
          onClick={this.onClick} />
        <CircleSpinner size={20} className={styles.thumbSpinner} />
      </div>
    )
  }
}
